import React, { useCallback, useEffect, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { Bell, Eye, RefreshCw } from 'react-feather';
import {
  fetchNotificationSummary,
  fetchNotifications,
} from '../notificationUtils';
import { NotificationDetailsModal } from './modals/NotificationDetailsModal';

export const Notifications = () => {
  const [unreadOnly, setUnreadOnly] = useState(true);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    document.title = 'Notifications';
    window.scrollTo(0, 0);
  }, []);

  const summaryQuery = useQuery(
    'notificationSummary',
    fetchNotificationSummary,
    {
      refetchInterval: 60000,
      refetchOnWindowFocus: false,
    },
  );

  const notificationsQuery = useQuery(
    ['notifications', unreadOnly],
    () => fetchNotifications(unreadOnly),
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    },
  );

  const applyReadUpdate = useCallback(
    (readNotification) => {
      if (!readNotification?.id) {
        return;
      }

      queryClient.setQueryData(['notifications', true], (current) =>
        Array.isArray(current)
          ? current.filter(
              (notification) => notification.id !== readNotification.id,
            )
          : current,
      );

      queryClient.setQueryData(['notifications', false], (current) =>
        Array.isArray(current)
          ? current.map((notification) =>
              notification.id === readNotification.id
                ? readNotification
                : notification,
            )
          : current,
      );

      queryClient.setQueryData('notificationSummary', (current) => {
        if (!current) {
          return current;
        }

        const wasUnread = current.latestUnreadNotifications?.some(
          (notification) => notification.id === readNotification.id,
        );

        return {
          ...current,
          unreadCount: wasUnread
            ? Math.max((current.unreadCount || 0) - 1, 0)
            : current.unreadCount || 0,
          latestUnreadNotifications:
            current.latestUnreadNotifications?.filter(
              (notification) => notification.id !== readNotification.id,
            ) || [],
        };
      });

      setSelectedNotification((current) =>
        current?.id === readNotification.id ? readNotification : current,
      );
    },
    [queryClient],
  );

  const notifications = notificationsQuery.data || [];
  const unreadCount = summaryQuery.data?.unreadCount ?? 0;

  const openNotification = (notification) => {
    setSelectedNotification(notification);
  };

  const handleMarkedRead = (notification) => {
    applyReadUpdate(notification);
  };

  const handleRowKeyDown = (event, notification) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    openNotification(notification);
  };

  const handleRefresh = () => {
    summaryQuery.refetch();
    notificationsQuery.refetch();
  };

  return (
    <div className="container pt-3 pb-4">
      <div className="card shadow-sm border-0 overflow-hidden notification-page-card">
        <div className="tasks-hero d-flex align-items-center px-4 py-3">
          <div className="tasks-hero-icon d-flex align-items-center justify-content-center">
            <Bell size={22} />
          </div>
          <div className="ml-3">
            <p className="mb-1 text-uppercase text-white-50 small">
              Notifications
            </p>
            <h4 className="mb-0 text-white">
              Review your Realty task notifications.
            </h4>
          </div>
          <button
            type="button"
            className="btn btn-light btn-sm ml-auto tasks-refresh-btn"
            onClick={handleRefresh}
            disabled={summaryQuery.isFetching || notificationsQuery.isFetching}
          >
            <RefreshCw size={14} className="mr-1" />
            Refresh
          </button>
        </div>

        <div className="p-4">
          <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between">
            <div className="d-flex align-items-center">
              <div className="task-count-number d-flex align-items-center justify-content-center">
                {unreadCount}
              </div>
              <div className="ml-3">
                <p className="mb-1 text-muted small">
                  Unread notifications
                </p>
                <h5 className="mb-0">Latest notification activity.</h5>
              </div>
            </div>
            <div className="custom-control custom-switch mt-3 mt-md-0">
              <input
                type="checkbox"
                className="custom-control-input"
                id="unreadOnlySwitch"
                checked={unreadOnly}
                onChange={(event) => setUnreadOnly(event.target.checked)}
              />
              <label
                className="custom-control-label"
                htmlFor="unreadOnlySwitch"
              >
                Unread Notifications only
              </label>
            </div>
          </div>

          {notificationsQuery.isFetching && !notificationsQuery.data ? (
            <div className="d-flex align-items-center mt-4" style={{ gap: '12px' }}>
              <div
                className="spinner-border text-primary"
                role="status"
                style={{ width: '1.75rem', height: '1.75rem' }}
              >
                <span className="sr-only">Loading...</span>
              </div>
              <span className="text-muted">Loading notifications...</span>
            </div>
          ) : notificationsQuery.isError ? (
            <div className="alert alert-warning mt-4 mb-0" role="alert">
              Unable to load notifications right now.
            </div>
          ) : notifications.length === 0 ? (
            <p className="text-muted mt-4 mb-0">
              No notifications found for this filter.
            </p>
          ) : (
            <div className="notification-table mt-4">
              <div className="notification-table-head">
                <span>Raised Date</span>
                <span>Task Type</span>
                <span>Reminder Text</span>
                <span>Record Ref</span>
                <span>Read</span>
                <span>Actions</span>
              </div>

              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`notification-row ${
                    notification.isRead ? '' : 'notification-row--unread'
                  }`}
                  role="button"
                  tabIndex={0}
                  onClick={() => openNotification(notification)}
                  onKeyDown={(event) =>
                    handleRowKeyDown(event, notification)
                  }
                >
                  <span data-label="Raised Date">
                    {notification.raisedDate || '-'}
                  </span>
                  <span data-label="Task Type">
                    {notification.taskTypeName || '-'}
                  </span>
                  <span
                    data-label="Reminder Text"
                    className="notification-message-cell"
                  >
                    {notification.reminderText ||
                      notification.message ||
                      '-'}
                  </span>
                  <span data-label="Record Ref">
                    {notification.recordRef || '-'}
                  </span>
                  <span data-label="Read">
                    <span
                      className={`notification-read-badge ${
                        notification.isRead
                          ? 'notification-read-badge--read'
                          : 'notification-read-badge--unread'
                      }`}
                    >
                      {notification.isRead ? 'Read' : 'Unread'}
                    </span>
                  </span>
                  <span
                    data-label="Actions"
                    className="notification-actions"
                  >
                    <button
                      type="button"
                      className="btn btn-sm nav-icon-button task-action-btn"
                      onClick={(event) => {
                        event.stopPropagation();
                        openNotification(notification);
                      }}
                      title="Open"
                      aria-label="Open notification"
                    >
                      <Eye size={16} />
                    </button>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <NotificationDetailsModal
        show={Boolean(selectedNotification)}
        notification={selectedNotification}
        onClose={() => setSelectedNotification(null)}
        onMarkedRead={handleMarkedRead}
      />
    </div>
  );
};
